import { useEffect, useState } from "react";
import { api, API_BASE, getDepartmentFilter } from "@/lib/api";
import { crDateTime, crLocalToISO } from "@/lib/dateFormat";
import { useI18n } from "@/i18n/i18n";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Megaphone, Eye, Mail, Users, ImagePlus } from "lucide-react";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  { id: "student", label: "Estudiantes" },
  { id: "teacher", label: "Profesores" },
  { id: "academic_director", label: "Dirección académica" },
  { id: "superadmin", label: "Administradores" },
  { id: "affiliate", label: "Afiliados" },
];

const emptyForm = () => ({
  title: "", body: "", link: "", cta_label: "", image_url: "", roles: ["student", "teacher"],
  send_email: false, sede_id: "all", group_id: "all", publish_at: "",
});

export default function Announcements() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [sedes, setSedes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [readers, setReaders] = useState(null); // { ann, list, loading }
  const [uploading, setUploading] = useState(false);

  // Banner opcional: si no se sube nada, el anuncio sale como siempre.
  const uploadBanner = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/announcements/upload-image", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: r.data.url }));
      setPreview(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("error_generic"));
    } finally {
      setUploading(false);
    }
  };

  const openReaders = async (a) => {
    setReaders({ ann: a, list: [], loading: true });
    try {
      const r = await api.get(`/announcements/${a.id}/reads`);
      setReaders({ ann: a, list: r.data || [], loading: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
      setReaders(null);
    }
  };

  useEffect(() => {
    Promise.all([api.get("/sedes"), api.get("/groups")])
      .then(([s, g]) => { setSedes(s.data || []); setGroups(g.data || []); })
      .catch(() => {});
  }, []);

  const load = async () => {
    const r = await api.get("/announcements");
    setRows(r.data || []);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        title: form.title, body: form.body, link: form.link, cta_label: form.cta_label,
        image_url: form.image_url || null,
        roles: form.roles, send_email: form.send_email,
        sede_ids: form.sede_id !== "all" ? [form.sede_id] : null,
        group_ids: form.group_id !== "all" ? [form.group_id] : null,
        department_id: getDepartmentFilter() || null,
        publish_at: crLocalToISO(form.publish_at) || null,
      };
      const res = await api.post("/announcements", body);
      toast.success(res.data.published ? "Anuncio enviado" : "Anuncio programado");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (a) => {
    await api.patch(`/announcements/${a.id}`, { active: !a.active });
    load();
  };

  const remove = async (a) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/announcements/${a.id}`);
    toast.success(t("deleted"));
    load();
  };

  return (
    <div>
      <PageHeader
        title="Anuncios"
        subtitle="Avisá a estudiantes y profesores sobre novedades de la plataforma"
        testId="announcements-header"
        action={
          <Button onClick={() => { setForm(emptyForm()); setOpen(true); }} data-testid="new-announcement-button">
            <Plus className="h-4 w-4" /> Nuevo anuncio
          </Button>
        }
      />

      <div className="space-y-3" data-testid="announcements-list">
        {rows.map((a) => (
          <Card key={a.id} className="p-4 flex items-start gap-4" data-testid={`announcement-row-${a.id}`}>
            <Megaphone className={`h-5 w-5 mt-0.5 shrink-0 ${a.active ? "text-brand" : "text-slate-300"}`} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900">{a.title}</div>
              <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{a.body}</p>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px] text-slate-500">
                <span className="flex items-center gap-1"><Users className="h-3 w-3" />{a.roles.join(", ")}</span>
                <button
                  type="button"
                  onClick={() => openReaders(a)}
                  className="flex items-center gap-1 text-slate-500 hover:text-brand hover:underline"
                  title="Ver quiénes lo leyeron"
                  data-testid={`announcement-reads-${a.id}`}
                >
                  <Eye className="h-3 w-3" />{a.read_count || 0} / {a.target_count || 0} leídos
                </button>
                {a.send_email && (
                  <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{a.email_sent || 0} correos</span>
                )}
                {(a.group_ids || a.sede_ids) && (
                  <span className="text-indigo-600" data-testid={`announcement-scope-${a.id}`}>segmentado</span>
                )}
                {a.department_id && (
                  <span className="text-teal-700" data-testid={`announcement-dept-${a.id}`}>
                    {a.department_id === "dep_en" ? "Inglés" : "Español"}
                  </span>
                )}
                {a.published === false && a.publish_at && (
                  <span className="text-amber-600 font-semibold" data-testid={`announcement-scheduled-${a.id}`}>
                    programado: {crDateTime(a.publish_at)}
                  </span>
                )}
                {a.link && <span className="text-blue-600 truncate max-w-[180px]">{a.link}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Switch checked={a.active} onCheckedChange={() => toggleActive(a)} data-testid={`announcement-active-${a.id}`} />
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(a)} data-testid={`announcement-delete-${a.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </Card>
        ))}
        {rows.length === 0 && (
          <Card className="p-10 text-center text-slate-500" data-testid="announcements-empty">
            <Megaphone className="h-8 w-8 mx-auto mb-3 text-slate-300" />
            Todavía no enviaste anuncios
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="announcement-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Nuevo anuncio</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Título</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="announcement-title-input" />
            </div>
            <div>
              <Label>Mensaje</Label>
              <Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="announcement-body-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Enlace (opcional)</Label>
                <Input placeholder="/dashboard/resources" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} data-testid="announcement-link-input" />
              </div>
              <div>
                <Label>Texto del botón</Label>
                <Input placeholder="Ver Recursos" value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} data-testid="announcement-cta-input" />
              </div>
            </div>
            <div>
              <Label>Foto o banner (opcional)</Label>
              {form.image_url ? (
                <div className="mt-1.5 flex items-center gap-3">
                  <img src={`${API_BASE}${form.image_url}`} alt="" data-testid="announcement-banner-thumb"
                    className="h-16 w-28 object-cover rounded-md border border-slate-200" />
                  <Button variant="outline" size="sm" data-testid="remove-announcement-banner"
                    onClick={() => setForm({ ...form, image_url: "" })}>
                    <Trash2 className="h-3.5 w-3.5" /> Quitar
                  </Button>
                </div>
              ) : (
                <div className="mt-1.5">
                  <input type="file" accept="image/*" id="ann-banner" className="hidden"
                    onChange={uploadBanner} data-testid="announcement-banner-input" />
                  <Button variant="outline" size="sm" disabled={uploading}
                    data-testid="announcement-banner-button"
                    onClick={() => document.getElementById("ann-banner").click()}>
                    <ImagePlus className="h-4 w-4" /> {uploading ? "Subiendo..." : "Subir imagen"}
                  </Button>
                  <p className="text-[11px] text-slate-500 mt-1">
                    PNG, JPG, WEBP o GIF (máx. 8 MB). Si no subís nada, el anuncio sale como siempre.
                  </p>
                </div>
              )}
            </div>
            <div>
              <Label>¿Quiénes lo reciben?</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {ROLE_OPTIONS.map((r) => {
                  const on = form.roles.includes(r.id);
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setForm({ ...form, roles: on ? form.roles.filter((x) => x !== r.id) : [...form.roles, r.id] })}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${on ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                      data-testid={`announcement-role-${r.id}`}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Sede (opcional)</Label>
                <select
                  className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm"
                  value={form.sede_id}
                  onChange={(e) => setForm({ ...form, sede_id: e.target.value, group_id: "all" })}
                  data-testid="announcement-sede-select"
                >
                  <option value="all">Todas las sedes</option>
                  {sedes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Grupo (opcional)</Label>
                <select
                  className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm"
                  value={form.group_id}
                  onChange={(e) => setForm({ ...form, group_id: e.target.value })}
                  data-testid="announcement-group-select"
                >
                  <option value="all">Todos los grupos</option>
                  {groups
                    .filter((g) => form.sede_id === "all" || g.sede_id === form.sede_id)
                    .map((g) => <option key={g.id} value={g.id}>{g.name}{g.is_private ? " · privada" : ""}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Programar envío (opcional)</Label>
              <Input
                type="datetime-local"
                value={form.publish_at}
                onChange={(e) => setForm({ ...form, publish_at: e.target.value })}
                data-testid="announcement-publish-at"
              />
              <p className="text-[11px] text-slate-500 mt-1">Hora de Costa Rica. Si lo dejás vacío, sale ahora mismo. Si pones fecha, se publica solo (se revisa cada 10 minutos).</p>
            </div>
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
              <div>
                <Label htmlFor="ann-email">Enviar también por correo</Label>
                <p className="text-[11px] text-slate-500">Se envía una sola vez, al crear el anuncio</p>
              </div>
              <Switch id="ann-email" checked={form.send_email} onCheckedChange={(v) => setForm({ ...form, send_email: v })} data-testid="announcement-email-switch" />
            </div>
          </div>
          {preview && (
            <div className="border-t border-slate-200 pt-4" data-testid="announcement-preview">
              <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400 mb-2">
                Así lo verá cada persona
              </p>
              <div className="rounded-2xl border border-slate-200 bg-white shadow-lg p-5">
                <div className="flex items-center gap-2 text-brand">
                  <Megaphone className="h-5 w-5" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.14em]">Novedad</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 pt-1" data-testid="preview-title">
                  {form.title || "(sin título)"}
                </h3>
                {form.image_url && (
                  <img src={`${API_BASE}${form.image_url}`} alt="" data-testid="preview-image"
                    className="w-full max-h-56 object-cover rounded-xl border border-slate-200 mt-3" />
                )}
                <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed mt-2" data-testid="preview-body">
                  {form.body || "(sin mensaje)"}
                </p>
                <div className="flex justify-end pt-4">
                  {form.link.trim() ? (
                    <Button data-testid="preview-cta">{form.cta_label.trim() || "Ver ahora"}</Button>
                  ) : (
                    <Button variant="outline" data-testid="preview-ack">Entendido</Button>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-2" data-testid="preview-note">
                Lo reciben: {form.roles.length ? form.roles.map((r) => ROLE_OPTIONS.find((o) => o.id === r)?.label || r).join(", ") : "nadie todavía"}
                {form.link.trim() ? ` · el botón lleva a ${form.link.trim()}` : ""}
                {form.send_email ? " · también les llega por correo" : ""}
              </p>
            </div>
          )}
          <DialogFooter className="sticky bottom-0 bg-white pt-3 mt-2 border-t border-slate-100">
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button variant="outline" onClick={() => setPreview((v) => !v)} data-testid="preview-announcement-button">
              <Eye className="h-4 w-4" /> {preview ? "Ocultar vista previa" : "Vista previa"}
            </Button>
            <Button onClick={submit} disabled={saving} data-testid="save-announcement-button">
              {saving ? "Enviando..." : "Enviar anuncio"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!readers} onOpenChange={(o) => !o && setReaders(null)}>
        <DialogContent className="max-w-md" data-testid="announcement-readers-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-base">Leído por · {readers?.ann?.title}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto -mx-2 px-2">
            {readers?.loading && <p className="text-sm text-slate-500 py-6 text-center">Cargando...</p>}
            {!readers?.loading && (readers?.list || []).length === 0 && (
              <p className="text-sm text-slate-500 py-6 text-center" data-testid="announcement-readers-empty">
                Todavía nadie lo ha leído
              </p>
            )}
            <div className="divide-y divide-slate-100">
              {(readers?.list || []).map((u) => (
                <div key={u.user_id} className="py-2 flex items-center justify-between gap-3" data-testid={`announcement-reader-${u.user_id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{u.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{u.email} · {u.role}</div>
                  </div>
                  <div className="text-[11px] text-slate-400 shrink-0">
                    {u.read_at ? crDateTime(u.read_at) : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReaders(null)} data-testid="close-readers-button">{t("close") || "Cerrar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
